import React, { useState, useRef, useEffect } from "react";
import { Robot as RobotIcon, PaperPlaneRight as PaperPlaneRightIcon, Sparkle as SparkleIcon, User as UserIcon } from "@phosphor-icons/react";

const SUGGESTIONS = [
  "Which customers are over their credit limit?",
  "Suggest a restock plan for low inventory.",
  "Forecast next month's sales trend.",
  "How can I optimize delivery routes today?",
];

export default function Assistant() {
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! I'm the Lucas & Sons AI assistant. Ask me about sales, deliveries, credit, inventory or safety operations." },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef(null);
  const sessionId = useRef("chat-" + Date.now());

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput(""); setBusy(true);
    setMessages((m) => [...m, { role: "user", content: msg }, { role: "assistant", content: "" }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(localStorage.getItem("ls_token") ? { Authorization: `Bearer ${localStorage.getItem("ls_token")}` } : {}) },
        credentials: "include",
        body: JSON.stringify({ message: msg, session_id: sessionId.current }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        acc += decoder.decode(value, { stream: true });
        setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: acc }; return c; });
      }
    } catch (e) {
      setMessages((m) => { const c = [...m]; c[c.length - 1] = { role: "assistant", content: "Sorry, I couldn't reach the AI service." }; return c; });
    } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-8rem)] animate-fade-up">
      <div className="flex items-center gap-3 mb-4">
        <span className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center"><RobotIcon size={24} weight="duotone" /></span>
        <div><h1 className="font-head text-xl font-bold">AI Assistant</h1><p className="text-muted-foreground text-sm">Powered by live operations data</p></div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-4 border rounded-lg bg-card p-4">
        {messages.map((m, i) => (
          <div key={i} className={`flex gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
            <span className={`h-8 w-8 shrink-0 rounded-md flex items-center justify-center ${m.role === "user" ? "bg-secondary" : "bg-primary/10 text-primary"}`}>
              {m.role === "user" ? <UserIcon size={17} weight="duotone" /> : <SparkleIcon size={17} weight="duotone" />}
            </span>
            <div className={`rounded-lg px-3 py-2 text-sm max-w-[80%] whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>
              {m.content || <span className="text-muted-foreground animate-pulse">Thinking...</span>}
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>

      {messages.length <= 1 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {SUGGESTIONS.map((s) => (
            <button key={s} data-testid="ai-suggestion" onClick={() => send(s)} className="text-xs border rounded-full px-3 py-1.5 hover:border-primary hover:text-primary transition-colors">{s}</button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="mt-3 flex gap-2">
        <input
          data-testid="ai-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask about your operations..."
          className="flex-1 h-11 rounded-lg border bg-card px-4 text-sm focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none"
        />
        <button data-testid="ai-send" type="submit" disabled={busy} className="h-11 w-11 rounded-lg bg-primary text-primary-foreground flex items-center justify-center disabled:opacity-50 hover:bg-primary/90 transition-colors">
          <PaperPlaneRightIcon size={20} weight="fill" />
        </button>
      </form>
    </div>
  );
}
